import React, { useState } from 'react';
import { useAuth } from '../auth/context/AuthContext';
import { api } from '../api';
// import DefaultLayout from '../layout/DefaultLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CodeSection, VerificationStatus, CollapsedStatus, PricingData } from '../../shared/types';
import PricingInputGrid from '../components/PricingInputGrid';
import { PriceAnalysisDisplay } from '../components/PriceAnalysisDisplay';
import { analyzeHTMLForPricing } from '../utils/htmlAnalyzer';
import { getTestTableData, convertToPricingData } from '../utils/testTableData';
import { useEffect } from 'react';
import { LightAppPageShell } from '../layout/LightAppPageShell';

type UploadStep = 'upload' | 'pricing' | 'analysis' | 'markup';

const codeTextareaClassName =
  'min-h-[200px] font-mono text-sm border-gray-300 bg-white text-gray-900 placeholder:text-gray-400 focus-visible:ring-yellow-500/30 dark:border-gray-300 dark:bg-white dark:text-gray-900';

export function UploadPricingTablePage() {
  const { user } = useAuth();
  
  const [codeSections, setCodeSections] = useState<CodeSection>({
    html: '',
    css: '',
    js: ''
  });

  const [verificationStatus, setVerificationStatus] = useState<VerificationStatus>({
    html: false,
    css: false,
    js: false
  });

  const [collapsedStatus, setCollapsedStatus] = useState<CollapsedStatus>({
    html: false,
    css: false,
    js: false
  });

  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [currentStep, setCurrentStep] = useState<UploadStep>('upload');
  const [pricingData, setPricingData] = useState<PricingData | null>(null);
  const [analysisResults, setAnalysisResults] = useState<any>(null);
  const [isTestMode, setIsTestMode] = useState<boolean>(false);
  const [selectedTestFile, setSelectedTestFile] = useState<string>('table1_basic');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Auto-trigger autofill when test mode is enabled and test file is selected
  useEffect(() => {
    if (isTestMode && selectedTestFile && !pricingData) {
      console.log('🔄 useEffect: Auto-triggering autofill for', selectedTestFile);
      const testTableConfig = getTestTableData(selectedTestFile);
      if (testTableConfig) {
        console.log('✅ useEffect: Auto-autofilling for', selectedTestFile);
        const autofilledPricingData = convertToPricingData(testTableConfig);
        setPricingData(autofilledPricingData);
        console.log('💾 useEffect: Auto-autofilled pricing data set:', autofilledPricingData);
      }
    }
  }, [isTestMode, selectedTestFile, pricingData]);

  // Test mode function to load test files
  const loadTestFiles = async (testFile?: string) => {
    const fileToLoad = testFile || selectedTestFile;
    try {
      let htmlResponse, cssResponse, jsResponse;
      
      if (fileToLoad === 'table1_basic') {
        // Basic test file has separate HTML, CSS, and JS files
        [htmlResponse, cssResponse, jsResponse] = await Promise.all([
          fetch('/test_tables/table1_basic.html'),
          fetch('/test_tables/table1_basic.css'),
          fetch('/test_tables/table1_basic.js')
        ]);
      } else if (fileToLoad === 'table2_basic') {
        // Advanced test file with 4 tiers and 3 billing periods
        [htmlResponse, cssResponse, jsResponse] = await Promise.all([
          fetch('/test_tables/table2_basic.html'),
          fetch('/test_tables/table2_basic.css'),
          fetch('/test_tables/table2_basic.js')
        ]);
      } else if (fileToLoad === 'table3_enterprise') {
        // Enterprise test file with 3 tiers and 3 billing periods
        [htmlResponse, cssResponse, jsResponse] = await Promise.all([
          fetch('/test_tables/table3_enterprise.html'),
          fetch('/test_tables/table3_enterprise.css'),
          fetch('/test_tables/table3_enterprise.js')
        ]);
      } else {
        // Currency test files only have HTML content, no separate CSS/JS
        htmlResponse = await fetch(`/test_tables/${fileToLoad}`);
        cssResponse = { text: () => Promise.resolve('') };
        jsResponse = { text: () => Promise.resolve('') };
      }

      const html = await htmlResponse.text();
      const css = await cssResponse.text();
      const js = await jsResponse.text();

      setCodeSections({ html, css, js });
      
      // Auto-verify all sections in test mode
      setVerificationStatus({ html: true, css: true, js: true });
      
      console.log('Test files loaded successfully');
    } catch (error) {
      console.error('Failed to load test files:', error);
      alert('Failed to load test files. Please check the console for details.');
    }
  };

  const handleTestFileChange = (value: string) => {
    console.log('🔄 Test file changed from', selectedTestFile, 'to', value);
    setSelectedTestFile(value);
    // Clear existing pricing data when test file changes to allow re-autofill
    setPricingData(null);
    if (isTestMode) {
      loadTestFiles(value);
      // Trigger autofill after loading new test file
      setTimeout(() => {
        console.log('🔄 Triggering autofill for new test file:', value);
        const testTableConfig = getTestTableData(value);
        if (testTableConfig) {
          console.log('✅ Auto-autofilling for', value);
          const autofilledPricingData = convertToPricingData(testTableConfig);
          setPricingData(autofilledPricingData);
          console.log('💾 Auto-autofilled pricing data set:', autofilledPricingData);
        }
      }, 100); // Small delay to ensure files are loaded
    }
  };

  const toggleTestMode = () => {
    const newTestMode = !isTestMode;
    setIsTestMode(newTestMode);
    
    if (newTestMode) {
      console.log('🧪 Test Mode Enabled - Loading test files...');
      loadTestFiles(selectedTestFile);
    } else {
      console.log('🧪 Test Mode Disabled - Clearing all data');
      // Clear all data when exiting test mode
      setCodeSections({ html: '', css: '', js: '' });
      setVerificationStatus({ html: false, css: false, js: false });
      setCollapsedStatus({ html: false, css: false, js: false });
      setPreviewHtml('');
    }
  };

  const handleFileUpload = (type: keyof CodeSection, files: FileList | null) => {
    if (!files || files.length === 0) return;

    const file = files[0];
    const reader = new FileReader();

    reader.onload = (e) => {
      const content = e.target?.result as string;
      setCodeSections(prev => ({
        ...prev,
        [type]: content
      }));
    };

    reader.readAsText(file);
  };

  const handleCodeChange = (type: keyof CodeSection, value: string) => {
    setCodeSections(prev => ({
      ...prev,
      [type]: value
    }));
  };

  const handleVerificationToggle = (type: keyof CodeSection) => {
    setVerificationStatus(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const handleCollapseToggle = (type: keyof CodeSection) => {
    setCollapsedStatus(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const generatePreview = () => {
    const { html, css, js } = codeSections;
    const preview = `
      <!DOCTYPE html>
      <html>
      <head>
        <style>${css}</style>
      </head>
      <body>
        ${html}
        <script>${js}</script>
      </body>
      </html>
    `;
    setPreviewHtml(preview);
  };

  const handlePreviewConfirmed = () => {
    // Try to autofill pricing data ONLY in test mode
    if (isTestMode && !pricingData) {
      try {
        console.log('🧪 TEST MODE: Attempting to autofill pricing data...');
        console.log('🔍 Current selectedTestFile:', selectedTestFile);
        console.log('🔍 Available test files:', Object.keys(getTestTableData('table1_basic') ? { table1_basic: true, table2_basic: true, table3_enterprise: true } : {}));
        
        // First try to get predefined data for the selected test table
        const testTableConfig = getTestTableData(selectedTestFile);
        console.log('🔍 Test table config found:', !!testTableConfig);
        if (testTableConfig) {
          console.log('✅ TEST MODE: Using predefined data for', selectedTestFile);
          console.log('📊 Config details:', {
            name: testTableConfig.name,
            tiers: testTableConfig.tiers,
            billingPeriods: testTableConfig.billingPeriods
          });
          const autofilledPricingData = convertToPricingData(testTableConfig);
          setPricingData(autofilledPricingData);
          console.log('💾 TEST MODE: Predefined pricing data set:', autofilledPricingData);
        } else if (codeSections.html) {
          // Fallback to HTML analysis if no predefined data exists
          console.log('🧪 TEST MODE: No predefined data found, attempting HTML analysis...');
          const extractedData = analyzeHTMLForPricing(codeSections.html);
          
          if (extractedData.tiers > 0 && extractedData.billingPeriods.length > 0) {
            console.log('✅ TEST MODE: HTML analysis successful:', extractedData);
            
            // Convert extracted data to PricingData format
            const autofilledPricingData: PricingData = {
              tiers: extractedData.tiers,
              billingPeriods: extractedData.billingPeriods,
              pricingGrid: {}
            };
            
            // Populate pricing grid with detected prices
            for (let tier = 1; tier <= extractedData.tiers; tier++) {
              autofilledPricingData.pricingGrid[tier] = {};
              for (const period of extractedData.billingPeriods) {
                const detectedPrice = extractedData.detectedPrices[tier - 1]?.[period];
                autofilledPricingData.pricingGrid[tier][period] = {
                  currentPrice: detectedPrice?.price || '',
                  currentLink: detectedPrice?.link || '',
                  defaultPrice: detectedPrice?.price || '',
                  defaultLink: detectedPrice?.link || '',
                  testPrices: []
                };
              }
            }
            
            setPricingData(autofilledPricingData);
            console.log('💾 TEST MODE: HTML-extracted pricing data set:', autofilledPricingData);
          } else {
            console.log('⚠️ TEST MODE: HTML analysis failed - no tiers or billing periods detected');
          }
        } else {
          console.log('⚠️ TEST MODE: No HTML content available for analysis');
        }
      } catch (error) {
        console.error('❌ TEST MODE: Autofill error:', error);
      }
    } else if (!isTestMode) {
      console.log('ℹ️ Production mode: Autofill disabled - user must configure pricing manually');
    }
    
    setCurrentStep('pricing');
  };

  const handlePricingComplete = (data: PricingData) => {
    console.log('💾 Saving pricing data:', data);
    setPricingData(data);
    setCurrentStep('analysis');
  };

  const handleBackToUpload = () => {
    console.log('⬅️ Going back to upload step, pricing data preserved:', pricingData);
    setCurrentStep('upload');
  };

  // Create initial data for test mode ONLY
  const getInitialData = (): PricingData | undefined => {
    // Return existing pricing data if available
    if (pricingData) {
      return pricingData;
    }
    
    // Try to autofill ONLY in test mode
    if (isTestMode) {
      try {
        console.log('🧪 TEST MODE: Attempting autofill in getInitialData...');
        
        // First try to get predefined data for the selected test table
        console.log('🔍 getInitialData - selectedTestFile:', selectedTestFile);
        const testTableConfig = getTestTableData(selectedTestFile);
        console.log('🔍 getInitialData - testTableConfig found:', !!testTableConfig);
        if (testTableConfig) {
          console.log('✅ TEST MODE: Using predefined data in getInitialData for', selectedTestFile);
          console.log('📊 getInitialData - Config details:', {
            name: testTableConfig.name,
            tiers: testTableConfig.tiers,
            billingPeriods: testTableConfig.billingPeriods
          });
          const autofilledData = convertToPricingData(testTableConfig);
          console.log('✅ TEST MODE: Predefined data in getInitialData:', autofilledData);
          return autofilledData;
        } else if (codeSections.html) {
          // Fallback to HTML analysis if no predefined data exists
          console.log('🧪 TEST MODE: No predefined data found, attempting HTML analysis in getInitialData...');
          const extractedData = analyzeHTMLForPricing(codeSections.html);
          if (extractedData.tiers > 0 && extractedData.billingPeriods.length > 0) {
            const autofilledData: PricingData = {
              tiers: extractedData.tiers,
              billingPeriods: extractedData.billingPeriods,
              pricingGrid: {}
            };
            
            // Populate pricing grid
            for (let tier = 1; tier <= extractedData.tiers; tier++) {
              autofilledData.pricingGrid[tier] = {};
              for (const period of extractedData.billingPeriods) {
                const detectedPrice = extractedData.detectedPrices[tier - 1]?.[period];
                autofilledData.pricingGrid[tier][period] = {
                  currentPrice: detectedPrice?.price || '',
                  currentLink: detectedPrice?.link || '',
                  defaultPrice: detectedPrice?.price || '',
                  defaultLink: detectedPrice?.link || '',
                  testPrices: []
                };
              }
            }
            
            console.log('✅ TEST MODE: HTML-extracted data in getInitialData:', autofilledData);
            return autofilledData;
          }
        }
      } catch (error) {
        console.error('❌ TEST MODE: Autofill error in getInitialData:', error);
      }
    } else if (!isTestMode) {
      console.log('ℹ️ Production mode: Autofill disabled in getInitialData');
    }
    
    return undefined;
  };

  const handleAnalysisComplete = async () => {
    setIsLoading(true);
    try {
      // Perform price analysis using the algorithm
      if (pricingData && codeSections.html) {
        const response = await api.post('/pricing-tables/analyze', {
          html: codeSections.html,
          pricingData: pricingData
        });
        
        // Extract data from the API response
        const result = response.data.data;
        setAnalysisResults({
          originalHtml: codeSections.html,
          modifiedHtml: result.modifiedHtml,
          priceMatches: result.priceMatches || [],
          errors: result.errors || [],
          warnings: result.warnings || [],
          suggestions: result.suggestions || []
        });
      } else {
        // Fallback if no pricing data
        setAnalysisResults({
          originalHtml: codeSections.html,
          modifiedHtml: codeSections.html,
          priceMatches: [],
          errors: ['No pricing data provided for analysis'],
          warnings: [],
          suggestions: []
        });
      }
      
      setCurrentStep('markup');
    } catch (error) {
      console.error('Analysis failed:', error);
      alert('Analysis failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleMarkupConfirmed = async () => {
    setIsLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);
    
    try {
      // First, save the pricing table
      const uploadResponse = await api.post('/pricing-tables/upload', {
        name: `Pricing Table ${new Date().toLocaleDateString()}`,
        description: 'Uploaded pricing table',
        html_content: codeSections.html,
        css_content: codeSections.css,
        js_content: codeSections.js,
        placeholder_html_content: analysisResults?.modifiedHtml || codeSections.html, // Save processed HTML with placeholders
        pricing_data_json: pricingData, // Include the pricing data for IndepVar generation
        currency: 'USD',
        selected_purpose: 'pricing'
      });

      const uploadResult = uploadResponse.data;
      console.log('Pricing table saved:', uploadResult);
      
      // IndepVar creation is already handled in the upload process
      if (pricingData) {
        setSuccessMessage('Pricing table saved successfully! Independent variable data created. Redirecting to pricing tables...');
      } else {
        setSuccessMessage('Pricing table saved successfully! Redirecting to pricing tables...');
      }
      
      // Wait a moment to show success message, then redirect
      setTimeout(() => {
        // Reset to upload step
        setCurrentStep('upload');
        setCodeSections({ html: '', css: '', js: '' });
        setVerificationStatus({ html: false, css: false, js: false });
        setPricingData(null);
        setAnalysisResults(null);
        setSuccessMessage(null);
        setErrorMessage(null);
      }, 2000);
    } catch (error) {
      console.error('Failed to save pricing table:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Failed to save pricing table. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToAnalysis = () => {
    setCurrentStep('analysis');
  };

  const allVerified = verificationStatus.html && (verificationStatus.css || verificationStatus.js);

  const getPlaceholderText = (type: keyof CodeSection) => {
    switch (type) {
      case 'html':
        return `Paste your pricing table HTML here...

Instructions:
1. Copy the HTML code from your existing pricing table
2. Include the main structure with pricing tiers, features, and buttons
3. Make sure all elements are properly closed
4. You can also upload an HTML file using the upload button above

Example:
<div class="pricing-table">
  <div class="tier">
    <h3>Basic</h3>
    <div class="price">$9/month</div>
    <ul class="features">
      <li>Feature 1</li>
      <li>Feature 2</li>
    </ul>
    <button class="cta">Get Started</button>
  </div>
</div>`;

      case 'css':
        return `Paste your pricing table CSS here...

Instructions:
1. Copy the CSS styles for your pricing table
2. Include all necessary styling for layout, colors, and responsive design
3. You can also upload a CSS file using the upload button above
4. If you don't have separate CSS, you can check the "Skip CSS" option below

Example:
.pricing-table {
  display: flex;
  gap: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.tier {
  flex: 1;
  padding: 20px;
  border: 1px solid #ddd;
  border-radius: 8px;
}`;

      case 'js':
        return `Paste your pricing table JavaScript here...

Instructions:
1. Copy any JavaScript code that powers your pricing table
2. Include event handlers, animations, or interactive features
3. You can also upload a JS/TSX file using the upload button above
4. If you don't have JavaScript, you can check the "Skip JS" option below

Example:
document.addEventListener('DOMContentLoaded', function() {
  // Your pricing table JavaScript code here
  const buttons = document.querySelectorAll('.cta');
  buttons.forEach(button => {
    button.addEventListener('click', function() {
      // Handle button clicks
    });
  });
});`;

      default:
        return '';
    }
  };

  const getFileAcceptTypes = (type: keyof CodeSection) => {
    switch (type) {
      case 'html':
        return '.html,.htm';
      case 'css':
        return '.css';
      case 'js':
        return '.js,.tsx,.ts,.jsx';
      default:
        return '';
    }
  };

  // Render different steps
  if (currentStep === 'pricing') {
    return (
      <LightAppPageShell>
        <>
        <div className='mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
          <h2 className='text-title-md2 font-semibold text-gray-900'>
            Configure Pricing
          </h2>
          <div className="flex items-center space-x-2">
            {pricingData && (
              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                ✓ Data Preserved
              </span>
            )}
            {isTestMode && (
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                🧪 Test Mode - Auto-populated
              </span>
            )}
          </div>
        </div>
        <PricingInputGrid
          onComplete={handlePricingComplete}
          onBack={handleBackToUpload}
          initialData={getInitialData()}
          isTestMode={isTestMode}
        />
        </>
      </LightAppPageShell>
    );
  }

  if (currentStep === 'analysis') {
    return (
      <LightAppPageShell>
        <>
        <div className='mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
          <h2 className='text-title-md2 font-semibold text-gray-900'>
            Analysis & Markup
          </h2>
          <div className="flex items-center space-x-2">
            {isTestMode && (
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                🧪 Test Mode
              </span>
            )}
          </div>
        </div>
        <Card className="border border-gray-200 bg-white shadow-sm ring-1 ring-gray-900/5">
          <CardHeader>
            <CardTitle>Content Analysis</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4 text-gray-600">
              Your pricing table has been uploaded and configured. The system will now analyze your code and prepare it for A/B testing.
            </p>
            <div className="space-y-4">
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                <h4 className="mb-2 font-medium text-blue-900">Upload Summary:</h4>
                <ul className="space-y-1 text-sm text-blue-800">
                  <li>• HTML: {codeSections.html ? '✓ Uploaded' : '✗ Missing'}</li>
                  <li>• CSS: {codeSections.css ? '✓ Uploaded' : '✗ Missing'}</li>
                  <li>• JavaScript: {codeSections.js ? '✓ Uploaded' : '✗ Missing'}</li>
                  <li>• Tiers: {pricingData?.tiers || 0}</li>
                  <li>• Billing Periods: {pricingData?.billingPeriods?.join(', ') || 'None'}</li>
                </ul>
              </div>
              <div className="flex space-x-3">
                <Button variant="outline" onClick={() => setCurrentStep('pricing')}>
                  Back to Pricing
                </Button>
                <Button onClick={handleAnalysisComplete} disabled={isLoading}>
                  {isLoading ? 'Processing...' : 'Continue to Markup'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        </>
      </LightAppPageShell>
    );
  }

  if (currentStep === 'markup' && analysisResults) {
    return (
      <LightAppPageShell>
        <>
        <div className='mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
          <h2 className='text-title-md2 font-semibold text-gray-900'>
            Price Analysis Results
          </h2>
          <div className="flex items-center space-x-2">
            {isTestMode && (
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                🧪 Test Mode
              </span>
            )}
          </div>
        </div>
        
        <PriceAnalysisDisplay
          originalHtml={analysisResults.originalHtml}
          modifiedHtml={analysisResults.modifiedHtml}
          priceMatches={analysisResults.priceMatches}
          errors={analysisResults.errors}
          warnings={analysisResults.warnings}
          suggestions={analysisResults.suggestions}
          onConfirm={handleMarkupConfirmed}
          onBack={handleBackToAnalysis}
          css={codeSections.css}
          js={codeSections.js}
        />
        </>
      </LightAppPageShell>
    );
  }

  // Default upload step
  return (
    <LightAppPageShell>
    <>
      <div className='mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
        <h2 className='text-title-md2 font-semibold text-gray-900'>
          Upload Your Pricing Table
        </h2>
        <div className="flex items-center space-x-3">
          <Label className="text-sm font-medium cursor-pointer">
            Test Mode
          </Label>
          <Checkbox
            checked={isTestMode}
            onCheckedChange={toggleTestMode}
          />
          {isTestMode && (
            <Select value={selectedTestFile} onValueChange={handleTestFileChange}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="table1_basic">table1_basic (Complete)</SelectItem>
                <SelectItem value="table2_basic">table2_basic (4 Tiers, 3 Billing)</SelectItem>
                <SelectItem value="table2_premium">table2_premium (Premium)</SelectItem>
                <SelectItem value="table3_enterprise">table3_enterprise (Enterprise)</SelectItem>
              </SelectContent>
            </Select>
          )}
          {isTestMode && (
            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
              ✓ Test Files Loaded
            </span>
          )}
        </div>
      </div>

      <div className='space-y-6'>
        {/* Test Mode Banner */}
        {isTestMode && (
          <Card className="border border-blue-200 bg-blue-50 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                <span className="font-medium text-blue-900">
                  Test Mode Active - Using {selectedTestFile}
                  {selectedTestFile === 'table1_basic' || selectedTestFile === 'table2_basic' ? ' (HTML + CSS + JS)' : ' (HTML only)'}
                </span>
              </div>
              <p className="mt-2 text-sm text-blue-800">
                All sections are automatically verified. Pricing data is pre-populated with test values.
                {selectedTestFile === 'table1_basic' && ' 3 tiers (Starter, Professional, Enterprise) with monthly and annual billing periods.'}
                {selectedTestFile === 'table2_basic' && ' 4 tiers (Starter, Professional, Business, Enterprise) with monthly, quarterly, and annual billing periods.'}
                {selectedTestFile !== 'table1_basic' && selectedTestFile !== 'table2_basic' && ' Various pricing configurations for testing.'}
              </p>
            </CardContent>
          </Card>
        )}

        {/* HTML Section */}
        <Card className={`border border-gray-200 bg-white shadow-sm ring-1 ring-gray-900/5 transition-all duration-300 dark:border-gray-200 dark:bg-white ${collapsedStatus.html ? 'opacity-75' : ''} ${isTestMode ? 'border-blue-300 ring-blue-200/50' : ''}`}>
          <CardHeader 
            className='cursor-pointer rounded-t-lg transition-colors hover:bg-gray-50'
            onClick={() => handleCollapseToggle('html')}
          >
            <div className='flex items-center justify-between'>
              <div className='flex items-center space-x-3'>
                <Checkbox
                  checked={verificationStatus.html}
                  onCheckedChange={() => handleVerificationToggle('html')}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  disabled={isTestMode}
                />
                <CardTitle className='flex items-center space-x-2'>
                  <span className='text-blue-600'>HTML</span>
                  {isTestMode && <span className='text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full'>Test File</span>}
                  {verificationStatus.html && (
                    <span className='text-sm text-green-600'>✓ Verified</span>
                  )}
                </CardTitle>
              </div>
              <div className='flex items-center space-x-2'>
                <span className='text-sm text-gray-500'>
                  {collapsedStatus.html ? 'Click to expand' : 'Click to collapse'}
                </span>
                <svg 
                  className={`w-5 h-5 transition-transform ${collapsedStatus.html ? 'rotate-180' : ''}`}
                  fill='none' 
                  stroke='currentColor' 
                  viewBox='0 0 24 24'
                >
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
                </svg>
              </div>
            </div>
          </CardHeader>
          {!collapsedStatus.html && (
            <CardContent className='space-y-4'>
              {!isTestMode && (
                <div className='flex items-center space-x-4'>
                  <Label htmlFor='html-upload' className='cursor-pointer'>
                    <Button variant='outline' asChild>
                      <span>Upload HTML File</span>
                    </Button>
                  </Label>
                  <input
                    id='html-upload'
                    type='file'
                    accept={getFileAcceptTypes('html')}
                    onChange={(e) => handleFileUpload('html', e.target.files)}
                    className='hidden'
                  />
                  <span className='text-sm text-gray-500'>or paste your HTML code below</span>
                </div>
              )}
              <Textarea
                placeholder={getPlaceholderText('html')}
                value={codeSections.html}
                onChange={(e) => handleCodeChange('html', e.target.value)}
                className={codeTextareaClassName}
                disabled={isTestMode}
              />
            </CardContent>
          )}
        </Card>

        {/* CSS Section */}
        <Card className={`border border-gray-200 bg-white shadow-sm ring-1 ring-gray-900/5 transition-all duration-300 dark:border-gray-200 dark:bg-white ${collapsedStatus.css ? 'opacity-75' : ''} ${isTestMode ? 'border-blue-300 ring-blue-200/50' : ''}`}>
          <CardHeader 
            className='cursor-pointer rounded-t-lg transition-colors hover:bg-gray-50'
            onClick={() => handleCollapseToggle('css')}
          >
            <div className='flex items-center justify-between'>
              <div className='flex items-center space-x-3'>
                <Checkbox
                  checked={verificationStatus.css}
                  onCheckedChange={() => handleVerificationToggle('css')}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  disabled={isTestMode}
                />
                <CardTitle className='flex items-center space-x-2'>
                  <span className='text-green-600'>CSS</span>
                  {isTestMode && <span className='text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full'>Test File</span>}
                  {verificationStatus.css && (
                    <span className='text-sm text-green-600'>✓ Verified</span>
                  )}
                </CardTitle>
              </div>
              <div className='flex items-center space-x-2'>
                <span className='text-sm text-gray-500'>
                  {collapsedStatus.css ? 'Click to expand' : 'Click to collapse'}
                </span>
                <svg 
                  className={`w-5 h-5 transition-transform ${collapsedStatus.css ? 'rotate-180' : ''}`}
                  fill='none' 
                  stroke='currentColor' 
                  viewBox='0 0 24 24'
                >
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
                </svg>
              </div>
            </div>
          </CardHeader>
          {!collapsedStatus.css && (
            <CardContent className='space-y-4'>
              {!isTestMode && (
                <div className='flex items-center space-x-4'>
                  <Label htmlFor='css-upload' className='cursor-pointer'>
                    <Button variant='outline' asChild>
                      <span>Upload CSS File</span>
                    </Button>
                  </Label>
                  <input
                    id='css-upload'
                    type='file'
                    accept={getFileAcceptTypes('css')}
                    onChange={(e) => handleFileUpload('css', e.target.files)}
                    className='hidden'
                  />
                  <span className='text-sm text-gray-500'>or paste your CSS code below</span>
                </div>
              )}
              <Textarea
                placeholder={getPlaceholderText('css')}
                value={codeSections.css}
                onChange={(e) => handleCodeChange('css', e.target.value)}
                className={codeTextareaClassName}
                disabled={isTestMode}
              />
            </CardContent>
          )}
        </Card>

        {/* JavaScript Section */}
        <Card className={`border border-gray-200 bg-white shadow-sm ring-1 ring-gray-900/5 transition-all duration-300 dark:border-gray-200 dark:bg-white ${collapsedStatus.js ? 'opacity-75' : ''} ${isTestMode ? 'border-blue-300 ring-blue-200/50' : ''}`}>
          <CardHeader 
            className='cursor-pointer rounded-t-lg transition-colors hover:bg-gray-50'
            onClick={() => handleCollapseToggle('js')}
          >
            <div className='flex items-center justify-between'>
              <div className='flex items-center space-x-3'>
                <Checkbox
                  checked={verificationStatus.js}
                  onCheckedChange={() => handleVerificationToggle('js')}
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  disabled={isTestMode}
                />
                <CardTitle className='flex items-center space-x-2'>
                  <span className='text-yellow-600'>JavaScript</span>
                  {isTestMode && <span className='text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full'>Test File</span>}
                  {verificationStatus.js && (
                    <span className='text-sm text-green-600'>✓ Verified</span>
                  )}
                </CardTitle>
              </div>
              <div className='flex items-center space-x-2'>
                <span className='text-sm text-gray-500'>
                  {collapsedStatus.js ? 'Click to expand' : 'Click to collapse'}
                </span>
                <svg 
                  className={`w-5 h-5 transition-transform ${collapsedStatus.js ? 'rotate-180' : ''}`}
                  fill='none' 
                  stroke='currentColor' 
                  viewBox='0 0 24 24'
                >
                  <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M19 9l-7 7-7-7' />
                </svg>
              </div>
            </div>
          </CardHeader>
          {!collapsedStatus.js && (
            <CardContent className='space-y-4'>
              {!isTestMode && (
                <div className='flex items-center space-x-4'>
                  <Label htmlFor='js-upload' className='cursor-pointer'>
                    <Button variant='outline' asChild>
                      <span>Upload JS/TSX File</span>
                    </Button>
                  </Label>
                  <input
                    id='js-upload'
                    type='file'
                    accept={getFileAcceptTypes('js')}
                    onChange={(e) => handleFileUpload('js', e.target.files)}
                    className='hidden'
                  />
                  <span className='text-sm text-gray-500'>or paste your JavaScript code below</span>
                </div>
              )}
              <Textarea
                placeholder={getPlaceholderText('js')}
                value={codeSections.js}
                onChange={(e) => handleCodeChange('js', e.target.value)}
                className={codeTextareaClassName}
                disabled={isTestMode}
              />
            </CardContent>
          )}
        </Card>

        {/* Preview Section */}
        {allVerified && (
          <Card className='border border-green-200 bg-green-50 shadow-sm'>
            <CardHeader>
              <CardTitle className='text-green-900'>
                Preview Your Pricing Table
              </CardTitle>
            </CardHeader>
            <CardContent className='space-y-4'>
              <p className='text-green-800'>
                All sections have been verified! Click the button below to generate a preview of your pricing table.
              </p>
              <Button 
                onClick={generatePreview}
                className='bg-green-600 hover:bg-green-700 text-white'
              >
                Generate Preview
              </Button>
              
              {previewHtml && (
                <div className='mt-6'>
                  <h4 className='mb-3 font-medium text-gray-900'>
                    Preview:
                  </h4>
                  <div className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
                    <iframe
                      srcDoc={previewHtml}
                      className='w-full h-96 border-0'
                      title='Pricing Table Preview'
                    />
                  </div>
                  <div className='mt-4 flex space-x-3'>
                    <Button 
                      variant='outline'
                      onClick={handlePreviewConfirmed}
                    >
                      Looks Good - Continue
                    </Button>
                    <Button variant='outline'>
                      Make Changes
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Progress Indicator */}
        <div className='flex items-center justify-center space-x-4 rounded-lg border border-gray-200 bg-white p-4 text-gray-800 shadow-sm'>
          <div className='flex items-center space-x-2'>
            <div className={`w-3 h-3 rounded-full ${verificationStatus.html ? 'bg-green-500' : 'bg-gray-300'}`} />
            <span className='text-sm'>HTML</span>
          </div>
          <div className='flex items-center space-x-2'>
            <div className={`w-3 h-3 rounded-full ${verificationStatus.css ? 'bg-green-500' : 'bg-gray-300'}`} />
            <span className='text-sm'>CSS</span>
          </div>
          <div className='flex items-center space-x-2'>
            <div className={`w-3 h-3 rounded-full ${verificationStatus.js ? 'bg-green-500' : 'bg-gray-300'}`} />
            <span className='text-sm'>JavaScript</span>
          </div>
          {allVerified && (
            <div className='flex items-center space-x-2'>
              <div className='w-3 h-3 rounded-full bg-green-500' />
              <span className='text-sm font-medium text-green-600'>Ready for Preview</span>
            </div>
          )}
        </div>
      </div>
    </>
    </LightAppPageShell>
  );
}
